
// ============================================================
// PickupDeliverView — Mobile-First Order Form for "Pickup & Deliver"
//
// The on-demand fetch service: users have already made a purchase
// elsewhere (or need an item transported) and just need a courier
// to physically pick it up and drop it off.
//
// Flow: Category Selection (6 options: Parcel, Food, Grocery,
//   Pharmacy, Courier, Errand)
//   → Dynamic Order Form (common addresses + category-specific fields)
//   → Upload Receipt/Photo (mandatory for most)
//   → Quote & Confirm → Track
//
// Unlike "Order & Deliver" (storefront with SKUs + MOQs), this is a
// "Describe, Locate, Upload, and Go" workflow. The category defines
// the handling requirements, vehicle type, and urgency.
//
// Critical UX rules (from DI-Pickup-Deliver-ORDER DESIGN.md):
//   Rule A — Photo Upload Mandate:
//     Food/Grocery/Pharmacy → receipt photo mandatory.
//     Parcel/Courier/Errand → item/package photo mandatory.
//     Submit blocked + shake + hint if missing.
//   Rule B — Dynamic Vehicle Assignment:
//     Parcel/Grocery/Courier → Car/Van.
//     Food/Pharmacy → Motorcycle/Bicycle (faster).
//     Errand → Any available.
//   Rule C — Weight vs Distance pricing matrix shown transparently.
//   Rule D — Scheduling vs Instant toggle for Errand & Courier.
// ============================================================

import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { PageHeader } from "@/components/features/states";
import { currencySymbol, formatPrice } from "@/lib/format";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Package,
  Utensils,
  ShoppingCart,
  Pill,
  FileText,
  ListChecks,
  Camera,
  Upload,
  ImageIcon,
  X,
  Bike,
  Car,
  Truck,
  Zap,
  Calendar,
  Clock,
  ShieldCheck,
  Thermometer,
  AlertTriangle,
  CheckCircle2,
  Info,
  Repeat,
  ChevronRight,
  StickyNote,
  Phone,
  Weight,
  Ruler,
  Snowflake,
  Flame,
  Lock,
  PenLine,
  Store,
  Hash,
  PackageCheck,
  Bike as BikeIcon,
  Sparkles,
} from "lucide-react";

// ============================================================
// Category Catalog
// ============================================================

type CategoryId = "parcel" | "food" | "grocery" | "pharmacy" | "courier" | "errand";

type VehiclePref = "car_van" | "motorcycle" | "any";
type PhotoType = "item" | "receipt";
type Priority = "standard" | "high" | "highest";

interface CategoryMeta {
  id: CategoryId;
  labelKey: string;
  label: string;
  icon: typeof Package;
  emoji: string;
  blurb: string;
  color: string; // gradient classes
  accent: string; // text/border accent
  vehicle: VehiclePref;
  photoType: PhotoType;
  priority: Priority;
  supportsSchedule: boolean;
}

const CATEGORIES: Record<CategoryId, CategoryMeta> = {
  parcel: {
    id: "parcel",
    labelKey: "pickupDeliver.cat.parcel.label",
    label: "Parcel",
    icon: Package,
    emoji: "📦",
    blurb: "Documents, boxes, small packages",
    color: "from-amber-50 to-orange-100 dark:from-amber-950/30 dark:to-orange-900/20",
    accent: "text-amber-600 dark:text-amber-400",
    vehicle: "car_van",
    photoType: "item",
    priority: "standard",
    supportsSchedule: false,
  },
  food: {
    id: "food",
    labelKey: "pickupDeliver.cat.food.label",
    label: "Food",
    icon: Utensils,
    emoji: "🍕",
    blurb: "Takeout / restaurant meals",
    color: "from-rose-50 to-red-100 dark:from-rose-950/30 dark:to-red-900/20",
    accent: "text-rose-600 dark:text-rose-400",
    vehicle: "motorcycle",
    photoType: "receipt",
    priority: "high",
    supportsSchedule: false,
  },
  grocery: {
    id: "grocery",
    labelKey: "pickupDeliver.cat.grocery.label",
    label: "Grocery",
    icon: ShoppingCart,
    emoji: "🛒",
    blurb: "Already-bagged supermarket shopping",
    color: "from-emerald-50 to-green-100 dark:from-emerald-950/30 dark:to-green-900/20",
    accent: "text-emerald-600 dark:text-emerald-400",
    vehicle: "car_van",
    photoType: "receipt",
    priority: "high",
    supportsSchedule: false,
  },
  pharmacy: {
    id: "pharmacy",
    labelKey: "pickupDeliver.cat.pharmacy.label",
    label: "Pharmacy",
    icon: Pill,
    emoji: "💊",
    blurb: "Medicine / prescriptions (urgent)",
    color: "from-violet-50 to-purple-100 dark:from-violet-950/30 dark:to-purple-900/20",
    accent: "text-violet-600 dark:text-violet-400",
    vehicle: "motorcycle",
    photoType: "receipt",
    priority: "highest",
    supportsSchedule: false,
  },
  courier: {
    id: "courier",
    labelKey: "pickupDeliver.cat.courier.label",
    label: "Courier",
    icon: FileText,
    emoji: "📄",
    blurb: "Legal docs / business papers",
    color: "from-sky-50 to-blue-100 dark:from-sky-950/30 dark:to-blue-900/20",
    accent: "text-sky-600 dark:text-sky-400",
    vehicle: "car_van",
    photoType: "item",
    priority: "standard",
    supportsSchedule: true,
  },
  errand: {
    id: "errand",
    labelKey: "pickupDeliver.cat.errand.label",
    label: "Errand",
    icon: ListChecks,
    emoji: "🏃",
    blurb: "Dry cleaning, keys, gifts — anything",
    color: "from-teal-50 to-cyan-100 dark:from-teal-950/30 dark:to-cyan-900/20",
    accent: "text-teal-600 dark:text-teal-400",
    vehicle: "any",
    photoType: "item",
    priority: "standard",
    supportsSchedule: true,
  },
};

const CATEGORY_ORDER: CategoryId[] = ["parcel", "food", "grocery", "pharmacy", "courier", "errand"];

// ============================================================
// Pricing constants (in local currency units — Fdj for Djibouti launch)
// ============================================================

const BASE_FARE = 350; // base pickup fare
const PER_KM_RATE = 70; // per-km distance charge
const SIZE_SURCHARGE: Record<string, number> = { small: 0, medium: 100, large: 200 };
const URGENCY_SURCHARGE = 300; // express / urgent push-to-front
const FRAGILE_HANDLING = 50;
const THERMAL_BAG_FEE = 50;
const TEMPERATURE_CONTROL_FEE = 100;
const CONFIDENTIAL_FEE = 50;
const SERVICE_FEE_RATE = 0.05; // 5% platform fee
const SCHEDULE_DISCOUNT = 200; // save this much by scheduling vs instant

// Rough distance estimate from two address strings (no maps API client-side).
// Uses a simple hash-based pseudo-distance so the quote feels real without
// a network call. Real distance is computed server-side at confirmation.
function estimateDistanceKm(pickup: string, dropoff: string): number {
  if (!pickup.trim() || !dropoff.trim()) return 0;
  // Pseudo-distance: 2 km base + up to 8 km based on string-length delta.
  const lenDiff = Math.abs(pickup.length - dropoff.length);
  const charSum = (pickup + dropoff).length;
  return Math.max(2, Math.min(25, 2 + (charSum % 7) + lenDiff * 0.3));
}

// ============================================================
// Vehicle badge
// ============================================================

function VehicleBadge({ vehicle }: { vehicle: VehiclePref }) {
  const { t } = useTranslation();
  const config = {
    car_van: { icon: Car, label: t("pickupDeliver.vehicle.carVan"), cls: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" },
    motorcycle: { icon: Bike, label: t("pickupDeliver.vehicle.motorcycle"), cls: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300" },
    any: { icon: Truck, label: t("pickupDeliver.vehicle.any"), cls: "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300" },
  }[vehicle];
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`gap-1 border-0 ${config.cls}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

// ============================================================
// Category Tile (selector grid)
// ============================================================

function CategoryTile({ cat, onSelect }: { cat: CategoryMeta; onSelect: () => void }) {
  const { t } = useTranslation();
  const label = t(cat.labelKey);
  const blurb = t(`pickupDeliver.cat.${cat.id}.blurb`);
  const Icon = cat.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`group flex flex-col items-center gap-2 rounded-2xl border border-border bg-gradient-to-br ${cat.color} p-4 text-center transition hover:border-primary/50 hover:shadow-md active:scale-[0.97]`}
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-background/80 shadow-sm ring-1 ring-border/50">
        <Icon className={`h-7 w-7 ${cat.accent}`} />
      </span>
      <span className="text-sm font-bold leading-tight">{label}</span>
      <span className="line-clamp-2 text-[11px] text-muted-foreground">{blurb}</span>
      <span className="mt-auto flex items-center gap-1 pt-1">
        <VehicleBadge vehicle={cat.vehicle} />
      </span>
    </button>
  );
}

// ============================================================
// Photo Upload
// ============================================================

interface PhotoUploadProps {
  photoType: PhotoType;
  photoDataUrl: string | null;
  onChange: (dataUrl: string | null) => void;
}

function PhotoUpload({ photoType, photoDataUrl, onChange }: PhotoUploadProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("pickupDeliver.photoNotImage"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("pickupDeliver.photoTooLarge"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.readAsDataURL(file);
  };

  const isReceipt = photoType === "receipt";
  const hint = isReceipt
    ? t("pickupDeliver.photoHintReceipt")
    : t("pickupDeliver.photoHintItem");

  if (photoDataUrl) {
    return (
      <div className="relative overflow-hidden rounded-xl border-2 border-primary/40">
        <img src={photoDataUrl} alt={t("pickupDeliver.uploadedPhoto")} className="max-h-48 w-full object-cover" />
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground shadow-md transition hover:bg-background"
          aria-label={t("pickupDeliver.removePhoto")}
        >
          <X className="h-4 w-4" />
        </button>
        <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-lg bg-background/90 px-2 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {t("pickupDeliver.photoAttached")}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-dashed border-border bg-muted/20 p-4">
      <div className="flex gap-2">
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={() => cameraInputRef.current?.click()}
        >
          <Camera className="mr-2 h-4 w-4" />
          {t("pickupDeliver.takePhoto")}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mr-2 h-4 w-4" />
          {t("pickupDeliver.uploadImage")}
        </Button>
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {hint}
      </p>
    </div>
  );
}

// ============================================================
// Category-Specific Widgets
// ============================================================

// ---- Shared toggle row ----
function ToggleRow({
  icon: Icon,
  label,
  description,
  checked,
  onChange,
  accent = "text-primary",
}: {
  icon: typeof Weight;
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  accent?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="flex min-w-0 items-start gap-2">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${accent}`} />
        <div className="min-w-0">
          <p className="text-sm font-medium leading-tight">{label}</p>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

// ---- Parcel widget ----
interface ParcelDetails {
  size: "small" | "medium" | "large";
  weightKg: number;
  fragile: boolean;
  signatureRequired: boolean;
}

function ParcelWidget({ details, onChange }: { details: ParcelDetails; onChange: (d: ParcelDetails) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          <Ruler className="h-4 w-4 text-amber-600" /> {t("pickupDeliver.parcel.size")}
        </Label>
        <ToggleGroup
          type="single"
          value={details.size}
          onValueChange={(v) => v && onChange({ ...details, size: v as ParcelDetails["size"] })}
          className="grid w-full grid-cols-3 gap-2"
        >
          <ToggleGroupItem value="small" className="text-xs">📦 {t("pickupDeliver.parcel.sizeSmall")}</ToggleGroupItem>
          <ToggleGroupItem value="medium" className="text-xs">📦 {t("pickupDeliver.parcel.sizeMedium")}</ToggleGroupItem>
          <ToggleGroupItem value="large" className="text-xs">📦 {t("pickupDeliver.parcel.sizeLarge")}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div>
        <Label className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          <Weight className="h-4 w-4 text-amber-600" /> {t("pickupDeliver.parcel.weight")}: {details.weightKg} kg
        </Label>
        <Slider
          value={[details.weightKg]}
          min={0}
          max={50}
          step={1}
          onValueChange={([v]) => onChange({ ...details, weightKg: v })}
        />
      </div>
      <div className="divide-y divide-border">
        <ToggleRow
          icon={AlertTriangle}
          label={t("pickupDeliver.parcel.fragile")}
          description={t("pickupDeliver.parcel.fragileDesc")}
          checked={details.fragile}
          onChange={(v) => onChange({ ...details, fragile: v })}
          accent="text-amber-600"
        />
        <ToggleRow
          icon={PenLine}
          label={t("pickupDeliver.parcel.signature")}
          checked={details.signatureRequired}
          onChange={(v) => onChange({ ...details, signatureRequired: v })}
        />
      </div>
    </div>
  );
}

// ---- Food widget ----
interface FoodDetails {
  orderNumber: string;
  hotItems: boolean;
  coldItems: boolean;
  thermalBag: boolean;
  bagCount: number;
}

function FoodWidget({ details, onChange }: { details: FoodDetails; onChange: (d: FoodDetails) => void }) {
  const { t } = useTranslation();
  // Auto-toggle thermal bag when hot or cold items are selected — handled
  // in the toggle handlers (not an effect) to avoid cascading renders.
  const setHot = (v: boolean) => {
    const next = { ...details, hotItems: v };
    if (v && !next.thermalBag) next.thermalBag = true;
    onChange(next);
  };
  const setCold = (v: boolean) => {
    const next = { ...details, coldItems: v };
    if (v && !next.thermalBag) next.thermalBag = true;
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="food-order-num" className="flex items-center gap-1.5 text-sm font-medium">
          <Hash className="h-4 w-4 text-rose-600" /> {t("pickupDeliver.food.orderNumber")}
        </Label>
        <Input
          id="food-order-num"
          placeholder={t("pickupDeliver.food.orderNumberPlaceholder")}
          value={details.orderNumber}
          onChange={(e) => onChange({ ...details, orderNumber: e.target.value })}
        />
      </div>
      <div className="divide-y divide-border">
        <ToggleRow
          icon={Flame}
          label={t("pickupDeliver.food.hot")}
          checked={details.hotItems}
          onChange={setHot}
          accent="text-orange-500"
        />
        <ToggleRow
          icon={Snowflake}
          label={t("pickupDeliver.food.cold")}
          checked={details.coldItems}
          onChange={setCold}
          accent="text-sky-500"
        />
        <ToggleRow
          icon={Thermometer}
          label={t("pickupDeliver.food.thermalBag")}
          description={t("pickupDeliver.food.thermalBagDesc")}
          checked={details.thermalBag}
          onChange={(v) => onChange({ ...details, thermalBag: v })}
          accent="text-rose-600"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <ShoppingCart className="h-4 w-4 text-rose-600" /> {t("pickupDeliver.food.bagCount")}: {details.bagCount}
        </Label>
        <Slider
          value={[details.bagCount]}
          min={1}
          max={10}
          step={1}
          onValueChange={([v]) => onChange({ ...details, bagCount: v })}
        />
      </div>
    </div>
  );
}

// ---- Grocery widget ----
interface GroceryDetails {
  bagCount: number;
  heavyItems: boolean;
  frozenItems: boolean;
  storeName: string;
}

function GroceryWidget({ details, onChange }: { details: GroceryDetails; onChange: (d: GroceryDetails) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="grocery-store" className="flex items-center gap-1.5 text-sm font-medium">
          <Store className="h-4 w-4 text-emerald-600" /> {t("pickupDeliver.grocery.storeName")}
        </Label>
        <Input
          id="grocery-store"
          placeholder={t("pickupDeliver.grocery.storeNamePlaceholder")}
          value={details.storeName}
          onChange={(e) => onChange({ ...details, storeName: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="flex items-center gap-1.5 text-sm font-medium">
          <ShoppingCart className="h-4 w-4 text-emerald-600" /> {t("pickupDeliver.grocery.bagCount")}: {details.bagCount}
        </Label>
        <Slider
          value={[details.bagCount]}
          min={1}
          max={15}
          step={1}
          onValueChange={([v]) => onChange({ ...details, bagCount: v })}
        />
      </div>
      <div className="divide-y divide-border">
        <ToggleRow
          icon={Weight}
          label={t("pickupDeliver.grocery.heavy")}
          description={t("pickupDeliver.grocery.heavyDesc")}
          checked={details.heavyItems}
          onChange={(v) => onChange({ ...details, heavyItems: v })}
          accent="text-emerald-600"
        />
        <ToggleRow
          icon={Snowflake}
          label={t("pickupDeliver.grocery.frozen")}
          description={t("pickupDeliver.grocery.frozenDesc")}
          checked={details.frozenItems}
          onChange={(v) => onChange({ ...details, frozenItems: v })}
          accent="text-sky-500"
        />
      </div>
    </div>
  );
}

// ---- Pharmacy widget ----
interface PharmacyDetails {
  prescription: boolean;
  temperatureSensitive: boolean;
  urgent: boolean;
  pharmacyName: string;
}

function PharmacyWidget({ details, onChange }: { details: PharmacyDetails; onChange: (d: PharmacyDetails) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {details.urgent && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          <Zap className="h-4 w-4 shrink-0" />
          {t("pickupDeliver.pharmacy.urgentBanner")}
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="pharm-name" className="flex items-center gap-1.5 text-sm font-medium">
          <Store className="h-4 w-4 text-violet-600" /> {t("pickupDeliver.pharmacy.pharmacyName")}
        </Label>
        <Input
          id="pharm-name"
          placeholder={t("pickupDeliver.pharmacy.pharmacyNamePlaceholder")}
          value={details.pharmacyName}
          onChange={(e) => onChange({ ...details, pharmacyName: e.target.value })}
        />
      </div>
      <div className="divide-y divide-border">
        <ToggleRow
          icon={FileText}
          label={t("pickupDeliver.pharmacy.prescription")}
          description={t("pickupDeliver.pharmacy.prescriptionDesc")}
          checked={details.prescription}
          onChange={(v) => onChange({ ...details, prescription: v })}
          accent="text-violet-600"
        />
        <ToggleRow
          icon={Thermometer}
          label={t("pickupDeliver.pharmacy.tempSensitive")}
          description={t("pickupDeliver.pharmacy.tempSensitiveDesc")}
          checked={details.temperatureSensitive}
          onChange={(v) => onChange({ ...details, temperatureSensitive: v })}
          accent="text-sky-500"
        />
        <ToggleRow
          icon={Zap}
          label={t("pickupDeliver.pharmacy.urgent")}
          description={t("pickupDeliver.pharmacy.urgentDesc")}
          checked={details.urgent}
          onChange={(v) => onChange({ ...details, urgent: v })}
          accent="text-red-500"
        />
      </div>
    </div>
  );
}

// ---- Courier widget ----
interface CourierDetails {
  scheduleMode: "express" | "scheduled";
  scheduledTime: string;
  confidential: boolean;
  signatureRequired: boolean;
}

function CourierWidget({ details, onChange }: { details: CourierDetails; onChange: (d: CourierDetails) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          <Clock className="h-4 w-4 text-sky-600" /> {t("pickupDeliver.courier.deliveryTime")}
        </Label>
        <ToggleGroup
          type="single"
          value={details.scheduleMode}
          onValueChange={(v) => v && onChange({ ...details, scheduleMode: v as CourierDetails["scheduleMode"] })}
          className="grid w-full grid-cols-2 gap-2"
        >
          <ToggleGroupItem value="express" className="text-xs">
            <Zap className="mr-1 h-3.5 w-3.5" /> {t("pickupDeliver.courier.express")}
          </ToggleGroupItem>
          <ToggleGroupItem value="scheduled" className="text-xs">
            <Calendar className="mr-1 h-3.5 w-3.5" /> {t("pickupDeliver.courier.scheduled")}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {details.scheduleMode === "scheduled" && (
        <div className="space-y-1.5">
          <Label htmlFor="sched-time" className="flex items-center gap-1.5 text-sm font-medium">
            <Calendar className="h-4 w-4 text-sky-600" /> {t("pickupDeliver.courier.scheduledTime")}
          </Label>
          <Input
            id="sched-time"
            type="datetime-local"
            value={details.scheduledTime}
            onChange={(e) => onChange({ ...details, scheduledTime: e.target.value })}
          />
        </div>
      )}
      <div className="divide-y divide-border">
        <ToggleRow
          icon={Lock}
          label={t("pickupDeliver.courier.confidential")}
          description={t("pickupDeliver.courier.confidentialDesc")}
          checked={details.confidential}
          onChange={(v) => onChange({ ...details, confidential: v })}
          accent="text-sky-600"
        />
        <ToggleRow
          icon={PenLine}
          label={t("pickupDeliver.courier.signature")}
          checked={details.signatureRequired}
          onChange={(v) => onChange({ ...details, signatureRequired: v })}
        />
      </div>
    </div>
  );
}

// ---- Errand widget ----
interface ErrandDetails {
  pickupFrom: string;
  dropoffTo: string;
  taskDetails: string;
  scheduleMode: "express" | "scheduled";
  scheduledTime: string;
}

function ErrandWidget({ details, onChange }: { details: ErrandDetails; onChange: (d: ErrandDetails) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div>
        <Label className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          <Clock className="h-4 w-4 text-teal-600" /> {t("pickupDeliver.errand.timing")}
        </Label>
        <ToggleGroup
          type="single"
          value={details.scheduleMode}
          onValueChange={(v) => v && onChange({ ...details, scheduleMode: v as ErrandDetails["scheduleMode"] })}
          className="grid w-full grid-cols-2 gap-2"
        >
          <ToggleGroupItem value="express" className="text-xs">
            <Zap className="mr-1 h-3.5 w-3.5" /> {t("pickupDeliver.errand.rightNow")}
          </ToggleGroupItem>
          <ToggleGroupItem value="scheduled" className="text-xs">
            <Calendar className="mr-1 h-3.5 w-3.5" /> {t("pickupDeliver.errand.scheduleLater")}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {details.scheduleMode === "scheduled" && (
        <div className="space-y-1.5">
          <Label htmlFor="errand-sched" className="flex items-center gap-1.5 text-sm font-medium">
            <Calendar className="h-4 w-4 text-teal-600" /> {t("pickupDeliver.errand.scheduledTime")}
          </Label>
          <Input
            id="errand-sched"
            type="datetime-local"
            value={details.scheduledTime}
            onChange={(e) => onChange({ ...details, scheduledTime: e.target.value })}
          />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor="errand-pickup" className="flex items-center gap-1.5 text-sm font-medium">
          <MapPin className="h-4 w-4 text-teal-600" /> {t("pickupDeliver.errand.pickupFrom")}
        </Label>
        <Input
          id="errand-pickup"
          placeholder={t("pickupDeliver.errand.pickupFromPlaceholder")}
          value={details.pickupFrom}
          onChange={(e) => onChange({ ...details, pickupFrom: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="errand-dropoff" className="flex items-center gap-1.5 text-sm font-medium">
          <MapPin className="h-4 w-4 text-teal-600" /> {t("pickupDeliver.errand.dropoffTo")}
        </Label>
        <Input
          id="errand-dropoff"
          placeholder={t("pickupDeliver.errand.dropoffToPlaceholder")}
          value={details.dropoffTo}
          onChange={(e) => onChange({ ...details, dropoffTo: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="errand-task" className="flex items-center gap-1.5 text-sm font-medium">
          <StickyNote className="h-4 w-4 text-teal-600" /> {t("pickupDeliver.errand.taskDetails")}
        </Label>
        <Textarea
          id="errand-task"
          rows={3}
          placeholder={t("pickupDeliver.errand.taskDetailsPlaceholder")}
          value={details.taskDetails}
          onChange={(e) => onChange({ ...details, taskDetails: e.target.value })}
        />
      </div>
    </div>
  );
}

// ============================================================
// Quote Breakdown
// ============================================================

interface QuoteLine {
  label: string;
  value: number;
  optional?: boolean;
}

function QuoteCard({
  lines,
  total,
  scheduleDiscount,
  isScheduled,
}: {
  lines: QuoteLine[];
  total: number;
  scheduleDiscount: number;
  isScheduled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 rounded-xl border border-border bg-muted/20 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-primary" /> {t("pickupDeliver.estimateTitle")}
      </p>
      <div className="space-y-1">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{l.label}</span>
            <span className={`tabular-nums ${l.value === 0 ? "text-emerald-600 dark:text-emerald-400" : ""}`}>
              {l.value === 0 ? t("pickupDeliver.free") : `+${formatPrice(l.value)}`}
            </span>
          </div>
        ))}
        {isScheduled && scheduleDiscount > 0 && (
          <div className="flex items-center justify-between text-sm text-emerald-600 dark:text-emerald-400">
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> {t("pickupDeliver.scheduleDiscount")}
            </span>
            <span className="tabular-nums">−{formatPrice(scheduleDiscount)}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between border-t border-border pt-2 text-base font-bold">
        <span>{t("pickupDeliver.totalEstimate")}</span>
        <span className="text-primary">{formatPrice(total)}</span>
      </div>
    </div>
  );
}

// ============================================================
// Repeat Last Request preset
// ============================================================

interface LastRequest {
  category: CategoryId;
  label: string;
  description: string;
  ago: string;
}

function loadLastRequest(): LastRequest | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem("pickupDeliver.lastRequest");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.category && parsed.label) return parsed;
  } catch {
    // ignore
  }
  return null;
}

function saveLastRequest(req: LastRequest) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem("pickupDeliver.lastRequest", JSON.stringify(req));
  } catch {
    // ignore
  }
}

function RepeatLastRequest({ onUse }: { onUse: (cat: CategoryId) => void }) {
  const { t } = useTranslation();
  // Lazy initializer reads localStorage once on first client render. No
  // effect needed — the preset is static for the lifetime of this mount.
  const [last] = useState<LastRequest | null>(() => loadLastRequest());

  if (!last) return null;
  const cat = CATEGORIES[last.category];
  const Icon = cat.icon;

  return (
    <Card className="overflow-hidden border-primary/30 bg-primary/5">
      <CardContent className="flex items-center gap-3 p-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${cat.color}`}>
          <Repeat className={`h-5 w-5 ${cat.accent}`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-primary">{t("pickupDeliver.repeatLast")}</p>
          <p className="truncate text-sm font-medium">
            {cat.emoji} {last.label}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {last.description} · {last.ago}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => onUse(last.category)}>
          {t("pickupDeliver.useSame")}
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Main View
// ============================================================

export function PickupDeliverView() {
  const { t } = useTranslation();
  const navigate = useUiStore((s) => s.navigate);
  const { user } = useAuthStore();
  const currency = currencySymbol(user?.countryCode);

  const [category, setCategory] = useState<CategoryId | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.sessionStorage.getItem("pickupDeliverCategory");
    if (stored && (CATEGORY_ORDER as string[]).includes(stored)) {
      window.sessionStorage.removeItem("pickupDeliverCategory");
      return stored as CategoryId;
    }
    return null;
  });

  const cat = category ? CATEGORIES[category] : null;

  // ---- Common fields ----
  const [pickupAddress, setPickupAddress] = useState("");
  const [pickupNotes, setPickupNotes] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [dropoffNotes, setDropoffNotes] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [contactPhone, setContactPhone] = useState(user?.phone || "");
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [mobileQuoteOpen, setMobileQuoteOpen] = useState(false);

  // ---- Category-specific detail state ----
  const [parcelDetails, setParcelDetails] = useState<ParcelDetails>({
    size: "small",
    weightKg: 5,
    fragile: false,
    signatureRequired: false,
  });
  const [foodDetails, setFoodDetails] = useState<FoodDetails>({
    orderNumber: "",
    hotItems: false,
    coldItems: false,
    thermalBag: false,
    bagCount: 1,
  });
  const [groceryDetails, setGroceryDetails] = useState<GroceryDetails>({
    bagCount: 2,
    heavyItems: false,
    frozenItems: false,
    storeName: "",
  });
  const [pharmacyDetails, setPharmacyDetails] = useState<PharmacyDetails>({
    prescription: false,
    temperatureSensitive: false,
    urgent: false,
    pharmacyName: "",
  });
  const [courierDetails, setCourierDetails] = useState<CourierDetails>({
    scheduleMode: "express",
    scheduledTime: "",
    confidential: false,
    signatureRequired: false,
  });
  const [errandDetails, setErrandDetails] = useState<ErrandDetails>({
    pickupFrom: "",
    dropoffTo: "",
    taskDetails: "",
    scheduleMode: "express",
    scheduledTime: "",
  });

  const selectCategory = (id: CategoryId | null) => {
    setCategory(id);
    // Reset photo when switching categories (different photo type required).
    setPhotoDataUrl(null);
  };

  // ---- Quote calculation ----
  const distanceKm = useMemo(
    () => estimateDistanceKm(pickupAddress, dropoffAddress),
    [pickupAddress, dropoffAddress]
  );

  const isScheduled = useMemo(() => {
    if (!cat) return false;
    if (cat.id === "courier") return courierDetails.scheduleMode === "scheduled";
    if (cat.id === "errand") return errandDetails.scheduleMode === "scheduled";
    return false;
  }, [cat, courierDetails, errandDetails]);

  const { quoteLines, quoteTotal } = useMemo(() => {
    if (!cat) return { quoteLines: [] as QuoteLine[], quoteTotal: 0 };
    const lines: QuoteLine[] = [];
    const baseFare = BASE_FARE;
    const distanceFare = Math.round(distanceKm * PER_KM_RATE);
    lines.push({ label: t("pickupDeliver.baseFare", { km: distanceKm.toFixed(1) }), value: baseFare });
    lines.push({ label: t("pickupDeliver.distanceCharge", { km: distanceKm.toFixed(1) }), value: distanceFare });

    let surcharges = 0;

    if (cat.id === "parcel") {
      const sizeSurcharge = SIZE_SURCHARGE[parcelDetails.size];
      if (sizeSurcharge > 0) {
        lines.push({ label: t(`pickupDeliver.parcel.sizeSurcharge.${parcelDetails.size}`), value: sizeSurcharge });
        surcharges += sizeSurcharge;
      }
      if (parcelDetails.fragile) {
        lines.push({ label: t("pickupDeliver.parcel.fragileFee"), value: FRAGILE_HANDLING });
        surcharges += FRAGILE_HANDLING;
      }
      if (parcelDetails.weightKg > 15) {
        const heavyFee = 100;
        lines.push({ label: t("pickupDeliver.heavyWeightFee"), value: heavyFee });
        surcharges += heavyFee;
      }
    }

    if (cat.id === "food" && foodDetails.thermalBag) {
      lines.push({ label: t("pickupDeliver.food.thermalBagFee"), value: THERMAL_BAG_FEE });
      surcharges += THERMAL_BAG_FEE;
    }

    if (cat.id === "grocery" && groceryDetails.heavyItems) {
      const heavyFee = 80;
      lines.push({ label: t("pickupDeliver.grocery.heavyFee"), value: heavyFee });
      surcharges += heavyFee;
    }

    if (cat.id === "pharmacy") {
      if (pharmacyDetails.temperatureSensitive) {
        lines.push({ label: t("pickupDeliver.pharmacy.tempFee"), value: TEMPERATURE_CONTROL_FEE });
        surcharges += TEMPERATURE_CONTROL_FEE;
      }
      if (pharmacyDetails.urgent) {
        lines.push({ label: t("pickupDeliver.urgencyFee"), value: URGENCY_SURCHARGE });
        surcharges += URGENCY_SURCHARGE;
      }
    }

    if (cat.id === "courier") {
      if (courierDetails.confidential) {
        lines.push({ label: t("pickupDeliver.courier.confidentialFee"), value: CONFIDENTIAL_FEE });
        surcharges += CONFIDENTIAL_FEE;
      }
      if (courierDetails.scheduleMode === "express") {
        lines.push({ label: t("pickupDeliver.expressFee"), value: URGENCY_SURCHARGE });
        surcharges += URGENCY_SURCHARGE;
      }
    }

    if (cat.id === "errand" && errandDetails.scheduleMode === "express") {
      lines.push({ label: t("pickupDeliver.expressFee"), value: URGENCY_SURCHARGE });
      surcharges += URGENCY_SURCHARGE;
    }

    let total = baseFare + distanceFare + surcharges;
    if (isScheduled) total -= SCHEDULE_DISCOUNT;

    return { quoteLines: lines, quoteTotal: Math.max(0, total) };
  }, [cat, distanceKm, parcelDetails, foodDetails, groceryDetails, pharmacyDetails, courierDetails, errandDetails, isScheduled, t]);

  // ---- Validation ----
  const hasAddresses = pickupAddress.trim().length > 4 && dropoffAddress.trim().length > 4;
  const hasDescription = itemDescription.trim().length > 2 || cat?.id === "errand";
  const hasPhoto = photoDataUrl !== null;
  const canSubmit = hasAddresses && hasDescription && hasPhoto && !loading;

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  // ---- Submit ----
  const confirmAndRequest = async () => {
    if (!cat || !canSubmit) {
      if (!hasPhoto) {
        toast.error(t("pickupDeliver.photoRequired"));
        triggerShake();
      } else if (!hasAddresses) {
        toast.error(t("pickupDeliver.addressesRequired"));
      }
      return;
    }

    setLoading(true);

    // Build category-specific metadata.
    const categoryMeta: Record<string, unknown> = {};
    if (cat.id === "parcel") {
      categoryMeta.parcel = parcelDetails;
    } else if (cat.id === "food") {
      categoryMeta.food = foodDetails;
    } else if (cat.id === "grocery") {
      categoryMeta.grocery = groceryDetails;
    } else if (cat.id === "pharmacy") {
      categoryMeta.pharmacy = pharmacyDetails;
    } else if (cat.id === "courier") {
      categoryMeta.courier = courierDetails;
    } else if (cat.id === "errand") {
      categoryMeta.errand = errandDetails;
    }

    const metadata = {
      module: "pickup_deliver",
      category: cat.id,
      categoryLabel: t(cat.labelKey),
      vehicle: cat.vehicle,
      priority: cat.priority,
      photoType: cat.photoType,
      photoAttached: hasPhoto,
      // Store photo data URL reference (truncated for metadata size limits).
      photoRef: photoDataUrl ? `data:image/...(${photoDataUrl.length} chars)` : null,
      distanceKm,
      isScheduled,
      ...categoryMeta,
      pricing: {
        baseFare: BASE_FARE,
        distanceKm,
        distanceFare: Math.round(distanceKm * PER_KM_RATE),
        serviceFeeRate: SERVICE_FEE_RATE,
        estimate: quoteTotal,
        currency,
        scheduleDiscount: isScheduled ? SCHEDULE_DISCOUNT : 0,
      },
      source: "pickup_deliver_form",
    };

    const parcelDescription = cat.id === "errand"
      ? `${cat.emoji} ${t(cat.labelKey)}: ${errandDetails.taskDetails.slice(0, 60) || errandDetails.pickupFrom}`
      : `${cat.emoji} ${t(cat.labelKey)} — ${itemDescription.slice(0, 80)}`;

    const vehicleRequired =
      cat.vehicle === "motorcycle" ? "motorcycle"
      : cat.vehicle === "car_van" ? "car"
      : "any";

    const res = await api.jobs.create({
      parcelDescription,
      parcelCategory: cat.id,
      parcelWeightKg: cat.id === "parcel" ? parcelDetails.weightKg : 0,
      pickupAddress: cat.id === "errand" && errandDetails.pickupFrom
        ? `${errandDetails.pickupFrom} — ${pickupAddress}`
        : pickupAddress.trim(),
      pickupNotes: pickupNotes.trim() || undefined,
      dropoffAddress: cat.id === "errand" && errandDetails.dropoffTo
        ? `${errandDetails.dropoffTo} — ${dropoffAddress}`
        : dropoffAddress.trim(),
      dropoffNotes: dropoffNotes.trim() || undefined,
      vehicleRequired,
      contactPhone: contactPhone.trim() || undefined,
      customerOffer: quoteTotal,
      mode: "pickup_deliver",
      metadata,
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || t("errors.generic"));
      return;
    }

    // Save last request for the "Repeat Last Request" preset.
    saveLastRequest({
      category: cat.id,
      label: t(cat.labelKey),
      description: parcelDescription,
      ago: t("pickupDeliver.justNow"),
    });

    toast.success(t("pickupDeliver.placedSuccess"));
    navigate(`app/jobs/${res.data!.job.id}`);
  };

  // ============================================================
  // Render: Category Selection Screen
  // ============================================================

  if (!cat) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 pb-32 sm:pb-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("app")}>
            <ArrowLeft className="h-4 w-4 rtl-flip" />
          </Button>
          <PageHeader
            title={t("pickupDeliver.title")}
            subtitle={t("pickupDeliver.categorySubtitle")}
          />
        </div>

        {/* Trust strip */}
        <div className="flex items-center justify-center gap-4 rounded-xl border border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> {t("pickupDeliver.verifiedCouriers")}
          </span>
          <span className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5 text-primary" /> {t("pickupDeliver.instantDispatch")}
          </span>
          <span className="flex items-center gap-1">
            <Truck className="h-3.5 w-3.5 text-primary" /> {t("pickupDeliver.realTimeTrack")}
          </span>
        </div>

        {/* Repeat Last Request preset */}
        <RepeatLastRequest onUse={(c) => selectCategory(c)} />

        {/* Category grid */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {CATEGORY_ORDER.map((id) => (
            <CategoryTile key={id} cat={CATEGORIES[id]} onSelect={() => selectCategory(id)} />
          ))}
        </div>

        {/* Base fare banner */}
        <div className="flex items-center justify-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
          <Zap className="h-4 w-4 text-primary" />
          <span className="text-muted-foreground">{t("pickupDeliver.baseFareBanner")}</span>
          <span className="font-bold text-primary">{formatPrice(BASE_FARE)}</span>
        </div>
      </div>
    );
  }

  // ============================================================
  // Render: Dynamic Order Form
  // ============================================================

  const CategoryIcon = cat.icon;
  const photoMandatory = true; // All categories require a photo per Rule A.

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-40 sm:pb-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => selectCategory(null)}
        >
          <ArrowLeft className="h-4 w-4 rtl-flip" />
        </Button>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${cat.color}`}>
            <CategoryIcon className={`h-5 w-5 ${cat.accent}`} />
          </span>
          <div className="min-w-0">
            <p className="truncate font-bold leading-tight">
              {t("pickupDeliver.formTitle")} → {t(cat.labelKey)}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <VehicleBadge vehicle={cat.vehicle} />
              {cat.priority === "highest" && (
                <Badge variant="outline" className="border-red-300 bg-red-50 px-1.5 text-[10px] text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                  <Zap className="mr-0.5 h-2.5 w-2.5" /> {t("pickupDeliver.priorityHighest")}
                </Badge>
              )}
              {cat.priority === "high" && (
                <Badge variant="outline" className="border-orange-300 bg-orange-50 px-1.5 text-[10px] text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-300">
                  {t("pickupDeliver.priorityHigh")}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => selectCategory(null)}>
          {t("pickupDeliver.change")}
        </Button>
      </div>

      {/* Pickup location */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-600 dark:bg-orange-950/40 dark:text-orange-400">
              A
            </span>
            <MapPin className="h-4 w-4 text-orange-500" /> {t("pickupDeliver.pickupLocation")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pickup-addr">{t("pickupDeliver.addressLabel")} *</Label>
            <Input
              id="pickup-addr"
              placeholder={t("pickupDeliver.pickupPlaceholder")}
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pickup-notes" className="flex items-center gap-1.5">
              <StickyNote className="h-3.5 w-3.5" /> {t("pickupDeliver.pickupNotesLabel")}
            </Label>
            <Input
              id="pickup-notes"
              placeholder={t("pickupDeliver.pickupNotesPlaceholder")}
              value={pickupNotes}
              onChange={(e) => setPickupNotes(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Dropoff location */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
              B
            </span>
            <MapPin className="h-4 w-4 text-primary" /> {t("pickupDeliver.dropoffLocation")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dropoff-addr">{t("pickupDeliver.addressLabel")} *</Label>
            <Input
              id="dropoff-addr"
              placeholder={t("pickupDeliver.dropoffPlaceholder")}
              value={dropoffAddress}
              onChange={(e) => setDropoffAddress(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dropoff-notes" className="flex items-center gap-1.5">
              <StickyNote className="h-3.5 w-3.5" /> {t("pickupDeliver.dropoffNotesLabel")}
            </Label>
            <Input
              id="dropoff-notes"
              placeholder={t("pickupDeliver.dropoffNotesPlaceholder")}
              value={dropoffNotes}
              onChange={(e) => setDropoffNotes(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Photo upload (mandatory) */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <ImageIcon className="h-4 w-4 text-primary" />
              {cat.photoType === "receipt"
                ? t("pickupDeliver.uploadReceipt")
                : t("pickupDeliver.uploadItem")}
              <Badge variant="outline" className="border-red-300 bg-red-50 px-1.5 text-[10px] text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                {t("pickupDeliver.mandatory")}
              </Badge>
            </div>
          </div>
          <PhotoUpload photoType={cat.photoType} photoDataUrl={photoDataUrl} onChange={setPhotoDataUrl} />
        </CardContent>
      </Card>

      {/* Item description (common, except errand which has its own task field) */}
      {cat.id !== "errand" && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4 text-primary" /> {t("pickupDeliver.itemDescription")}
            </div>
            <Textarea
              rows={2}
              placeholder={t("pickupDeliver.itemDescriptionPlaceholder", { cat: t(cat.labelKey) })}
              value={itemDescription}
              onChange={(e) => setItemDescription(e.target.value)}
            />
          </CardContent>
        </Card>
      )}

      {/* Category-specific widgets */}
      <Card className="border-primary/20">
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <CategoryIcon className={`h-4 w-4 ${cat.accent}`} />
            {t(cat.labelKey)} {t("pickupDeliver.detailsSuffix")}
          </div>
          {cat.id === "parcel" && <ParcelWidget details={parcelDetails} onChange={setParcelDetails} />}
          {cat.id === "food" && <FoodWidget details={foodDetails} onChange={setFoodDetails} />}
          {cat.id === "grocery" && <GroceryWidget details={groceryDetails} onChange={setGroceryDetails} />}
          {cat.id === "pharmacy" && <PharmacyWidget details={pharmacyDetails} onChange={setPharmacyDetails} />}
          {cat.id === "courier" && <CourierWidget details={courierDetails} onChange={setCourierDetails} />}
          {cat.id === "errand" && <ErrandWidget details={errandDetails} onChange={setErrandDetails} />}
        </CardContent>
      </Card>

      {/* Contact phone */}
      <Card>
        <CardContent className="space-y-1.5 p-4">
          <Label htmlFor="contact-phone" className="flex items-center gap-1.5 text-sm font-medium">
            <Phone className="h-4 w-4" /> {t("pickupDeliver.contactPhone")}
          </Label>
          <Input
            id="contact-phone"
            placeholder="+253 77 ..."
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          />
        </CardContent>
      </Card>

      {/* Desktop quote + confirm */}
      <Card className="hidden sm:block">
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <PackageCheck className="h-4 w-4 text-primary" /> {t("pickupDeliver.quoteReview")}
          </div>
          <QuoteCard
            lines={quoteLines}
            total={quoteTotal}
            scheduleDiscount={SCHEDULE_DISCOUNT}
            isScheduled={isScheduled}
          />
          <Button
            className={`w-full ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
            size="lg"
            disabled={!canSubmit}
            onClick={confirmAndRequest}
          >
            {loading ? t("pickupDeliver.requesting") : t("pickupDeliver.confirmRequest")}
            {!loading && canSubmit ? ` — ${formatPrice(quoteTotal)}` : ""}
          </Button>
          {!canSubmit && !loading && (
            <p className="text-center text-xs text-muted-foreground">
              {!hasAddresses
                ? t("pickupDeliver.fillAddressesFirst")
                : !hasPhoto
                  ? t("pickupDeliver.uploadPhotoFirst")
                  : t("pickupDeliver.completeFormFirst")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Mobile sticky bottom bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:hidden">
        <div className="mx-auto flex max-w-2xl flex-col gap-2">
          {/* Compact quote preview */}
          <button
            type="button"
            onClick={() => {
              // Toggle the inline mobile quote card below.
              setMobileQuoteOpen((v) => !v);
            }}
            className="flex items-center justify-between text-xs text-muted-foreground"
          >
            <span className="flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {t("pickupDeliver.estimateTitle")}
            </span>
            <span className="flex items-center gap-1">
              {isScheduled && (
                <span className="text-emerald-600 dark:text-emerald-400">
                  −{formatPrice(SCHEDULE_DISCOUNT)}
                </span>
              )}
              <span className="text-lg font-bold text-primary">{formatPrice(quoteTotal)}</span>
              <ChevronRight className={`h-3.5 w-3.5 transition ${mobileQuoteOpen ? "rotate-90" : ""}`} />
            </span>
          </button>
          {mobileQuoteOpen && (
            <QuoteCard
              lines={quoteLines}
              total={quoteTotal}
              scheduleDiscount={SCHEDULE_DISCOUNT}
              isScheduled={isScheduled}
            />
          )}
          {/* Schedule discount hint */}
          {cat.supportsSchedule && !isScheduled && (
            <button
              type="button"
              onClick={() => {
                if (cat.id === "courier") setCourierDetails((d) => ({ ...d, scheduleMode: "scheduled" }));
                if (cat.id === "errand") setErrandDetails((d) => ({ ...d, scheduleMode: "scheduled" }));
              }}
              className="flex items-center justify-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400"
            >
              <Calendar className="h-3.5 w-3.5" />
              {t("pickupDeliver.switchToScheduled", { save: formatPrice(SCHEDULE_DISCOUNT) })}
            </button>
          )}
          <Button
            className={`w-full ${shake ? "animate-[shake_0.4s_ease-in-out]" : ""}`}
            disabled={!canSubmit}
            onClick={confirmAndRequest}
          >
            {loading ? "…" : t("pickupDeliver.confirmRequest")}
            {!loading && canSubmit ? ` — ${formatPrice(quoteTotal)}` : ""}
          </Button>
        </div>
      </div>

      {/* Photo-missing hint (mobile) */}
      {!hasPhoto && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300 sm:hidden">
          <ImageIcon className="h-4 w-4 shrink-0" />
          <span>{t("pickupDeliver.photoRequiredHint")}</span>
        </div>
      )}
    </div>
  );
}
